import type {
  MultipartPartContract,
  MultipartUploadContract,
  PrivateAsset,
  PrivateAssetPage,
  PrivateAssetRole,
  PrivateAssetType,
} from '@nest-react/domain';
import { ApiRequestError } from '~/app/regionalApiClient';
import { hashFileSha256 } from '~/utils/sha256';
import { apiRequest } from '~/app/providers';

const ASSET_URL_CACHE_TTL_MS = 10 * 60 * 1000;
const assetUrlCache = new Map<
  string,
  { expiresAt: number; request: Promise<string> }
>();

export const setPrivateAssetFavorite = async (
  workspaceId: string,
  assetId: string,
  favorite: boolean
): Promise<void> => {
  await apiRequest(`/workspaces/${workspaceId}/assets/${assetId}/favorite`, {
    method: favorite ? 'POST' : 'DELETE',
  });
};

interface ResumeState {
  upload: MultipartUploadContract;
  fileName: string;
  size: number;
  checksum: string;
  assetType: PrivateAssetType;
  assetRole: PrivateAssetRole;
}

export interface UploadCallbacks {
  onHashProgress?: (value: number) => void;
  onUploadProgress?: (value: number) => void;
  signal?: AbortSignal;
  onUploadSession?: (uploadId: string) => void;
  agentChatSessionId?: string;
}

const stateKey = (
  workspaceId: string,
  checksum: string,
  assetType: PrivateAssetType,
  assetRole: PrivateAssetRole
): string => `mylo-upload:${workspaceId}:${checksum}:${assetType}:${assetRole}`;

const isExpiredUpload = (upload: MultipartUploadContract): boolean => {
  const expiresAt = Date.parse(upload.expiresAt);
  return !Number.isFinite(expiresAt) || expiresAt <= Date.now();
};

const isExpiredUploadError = (error: unknown): boolean =>
  error instanceof ApiRequestError &&
  (error.status === 404 ||
    error.status === 410 ||
    (error.status === 409 && /upload session expired/i.test(error.message)));

const uploadPrivateAssetAttempt = async (
  workspaceId: string,
  file: File,
  assetType: PrivateAssetType,
  assetRole: PrivateAssetRole,
  callbacks: UploadCallbacks,
  canRestartExpiredResume: boolean
): Promise<PrivateAsset> => {
  const checksum = await hashFileSha256(
    file,
    callbacks.onHashProgress,
    callbacks.signal
  );
  callbacks.signal?.throwIfAborted();
  const key = stateKey(workspaceId, checksum, assetType, assetRole);
  let resume: ResumeState | undefined;
  try {
    resume = JSON.parse(localStorage.getItem(key) || '') as ResumeState;
    if (
      resume.fileName !== file.name ||
      resume.size !== file.size ||
      resume.assetType !== assetType ||
      resume.assetRole !== assetRole ||
      isExpiredUpload(resume.upload)
    ) {
      localStorage.removeItem(key);
      resume = undefined;
    }
  } catch {
    resume = undefined;
  }
  let upload = resume?.upload;
  if (!upload) {
    upload = await apiRequest<MultipartUploadContract>(
      `/workspaces/${workspaceId}/assets/uploads`,
      {
        method: 'POST',
        signal: callbacks.signal,
        body: JSON.stringify({
          fileName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          checksumSha256: checksum,
          assetType,
          assetRole,
          ...(callbacks.agentChatSessionId
            ? { agentChatSessionId: callbacks.agentChatSessionId }
            : {}),
        }),
      }
    );
    if (upload.reused) return upload.asset;
    localStorage.setItem(
      key,
      JSON.stringify({
        upload,
        fileName: file.name,
        size: file.size,
        checksum,
        assetType,
        assetRole,
      })
    );
  }
  callbacks.onUploadSession?.(upload.uploadId);
  try {
    const completed = await apiRequest<MultipartPartContract[]>(
      `/workspaces/${workspaceId}/assets/uploads/${upload.uploadId}/parts`,
      { signal: callbacks.signal }
    );
    const completedNumbers = new Set(completed.map(part => part.partNumber));
    let completedBytes = completed.reduce((total, part) => {
      const start = (part.partNumber - 1) * upload.partSizeBytes;
      return total + Math.min(upload.partSizeBytes, file.size - start);
    }, 0);
    callbacks.onUploadProgress?.(completedBytes / file.size);
    for (
      let partNumber = 1;
      partNumber <= upload.expectedParts;
      partNumber += 1
    ) {
      if (completedNumbers.has(partNumber)) continue;
      if (callbacks.signal?.aborted)
        throw new DOMException('Upload paused', 'AbortError');
      const { url } = await apiRequest<{ url: string }>(
        `/workspaces/${workspaceId}/assets/uploads/${upload.uploadId}/parts/${partNumber}`,
        { method: 'POST', signal: callbacks.signal }
      );
      const start = (partNumber - 1) * upload.partSizeBytes;
      const blob = file.slice(
        start,
        Math.min(file.size, start + upload.partSizeBytes)
      );
      const response = await fetch(url, {
        method: 'PUT',
        body: blob,
        signal: callbacks.signal,
      });
      if (!response.ok) throw new Error(`Upload part ${partNumber} failed`);
      const etag = response.headers.get('etag');
      if (!etag) throw new Error('Object storage did not expose the part ETag');
      completed.push({ partNumber, etag });
      completedBytes += blob.size;
      callbacks.onUploadProgress?.(completedBytes / file.size);
    }
    const asset = await apiRequest<PrivateAsset>(
      `/workspaces/${workspaceId}/assets/uploads/${upload.uploadId}/complete`,
      {
        method: 'POST',
        signal: callbacks.signal,
        body: JSON.stringify({ parts: completed }),
      }
    );
    localStorage.removeItem(key);
    return asset;
  } catch (error) {
    if (!canRestartExpiredResume || !isExpiredUploadError(error)) throw error;
    localStorage.removeItem(key);
    return uploadPrivateAssetAttempt(
      workspaceId,
      file,
      assetType,
      assetRole,
      callbacks,
      false
    );
  }
};

export const uploadPrivateAsset = (
  workspaceId: string,
  file: File,
  assetType: PrivateAssetType,
  assetRole: PrivateAssetRole,
  callbacks: UploadCallbacks = {}
): Promise<PrivateAsset> =>
  uploadPrivateAssetAttempt(
    workspaceId,
    file,
    assetType,
    assetRole,
    callbacks,
    true
  );

export const cancelPrivateAssetUpload = async (
  workspaceId: string,
  uploadId: string
): Promise<void> => {
  await apiRequest(`/workspaces/${workspaceId}/assets/uploads/${uploadId}`, {
    method: 'DELETE',
  });
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (!key?.startsWith(`mylo-upload:${workspaceId}:`)) continue;
    try {
      const state = JSON.parse(localStorage.getItem(key) || '') as ResumeState;
      if (state.upload.uploadId === uploadId) localStorage.removeItem(key);
    } catch {
      localStorage.removeItem(key);
    }
  }
};

export const privateAssetUrl = async (
  workspaceId: string,
  assetId: string,
  download = false,
  original = false
): Promise<string> => {
  const cacheKey = `${workspaceId}:${assetId}:${original}`;
  if (!download) {
    const cached = assetUrlCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) return cached.request;
  }

  const params = new URLSearchParams();
  if (download) params.set('download', 'true');
  if (original) params.set('original', 'true');
  const query = params.size ? `?${params}` : '';
  const request = apiRequest<{ url: string; expiresIn?: number }>(
    `/workspaces/${workspaceId}/assets/${assetId}/url${query}`
  ).then(result => result.url);
  if (!download) {
    assetUrlCache.set(cacheKey, {
      expiresAt: Date.now() + ASSET_URL_CACHE_TTL_MS,
      request,
    });
    void request.catch(() => {
      if (assetUrlCache.get(cacheKey)?.request === request) {
        assetUrlCache.delete(cacheKey);
      }
    });
  }
  return request;
};

export const listPrivateAssets = (
  workspaceId: string,
  deleted = false,
  cursor?: string
): Promise<PrivateAssetPage> => {
  const params = new URLSearchParams({ deleted: String(deleted) });
  if (cursor) params.set('cursor', cursor);
  return apiRequest<PrivateAssetPage>(
    `/workspaces/${workspaceId}/assets?${params.toString()}`
  );
};

export const deletePrivateAsset = async (
  workspaceId: string,
  assetId: string
): Promise<void> => {
  await apiRequest(`/workspaces/${workspaceId}/assets/${assetId}`, {
    method: 'DELETE',
  });
  assetUrlCache.delete(`${workspaceId}:${assetId}:false`);
  assetUrlCache.delete(`${workspaceId}:${assetId}:true`);
};
